class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    if (this._cartIconAbortController) {
      this._cartIconAbortController.abort();
    }
    this._cartIconAbortController = new AbortController();
    const { signal } = this._cartIconAbortController;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.open(cartLink);
      },
      { signal }
    );
    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      },
      { signal }
    );
  }

  async open(triggeredBy) {
    if (typeof CartItems !== 'undefined' && CartItems.shouldRefreshCartDrawerOnOpen()) {
      await CartItems.syncCheckoutDiscounts();
      await CartItems.refreshCartDrawerSections();
      CartItems.clearCheckoutReturnFlags();
    }

    publish(PUB_SUB_EVENTS.modalToggle, { display: false });
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.querySelector('.drawer__inner-empty') ||
          document.getElementById('CartDrawer') ||
          this.querySelector('.drawer__inner') ||
          this;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    lockBodyScroll();
  }

  close() {
    publish(PUB_SUB_EVENTS.modalToggle,  { display: true });
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    unlockBodyScroll();
    CartItems?.refreshCartIconBubble?.({ debounce: false });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      window.refreshTrustpilotWidgets?.(document.getElementById('CartDrawer') || this);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (html == null || html === '') return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.refreshTrustpilotWidgets =
  window.refreshTrustpilotWidgets ||
  function refreshTrustpilotWidgets(root = document) {
    const scope = root instanceof Element ? root : document;
    const widgets = scope.querySelectorAll('.trustpilot-widget');
    if (!widgets.length) return;

    const tryLoad = () => {
      if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
        widgets.forEach((el) => {
          try {
            window.Trustpilot.loadFromElement(el, true);
          } catch (e) {
            // Ignore and retry below if needed
          }
        });
        return true;
      }
      return false;
    };

    if (tryLoad()) return;

    // Trustpilot script loads async; retry briefly until available.
    let attempts = 0;
    const maxAttempts = 20; // ~5s
    const interval = setInterval(() => {
      attempts += 1;
      if (tryLoad() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 250);
  };

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const headerSectionId = CartItems.getHeaderSectionId();
    const sections = [
      {
        id: 'CartDrawer',
        section: this.dataset.id,
        selector: '.drawer__inner',
      },
    ];

    if (headerSectionId) {
      sections.push({
        id: 'cart-icon-bubble',
        section: headerSectionId,
        useCartIconBubble: true,
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
